import React, { createContext, useState, useContext, useEffect } from "react";
import axios from "axios";
import { BASE_URL, API_TOKEN, SLINK } from "../config";

const CartContext = createContext();

export const CartProvider = ({ children }) => {
  const [cartProducts, setCartProducts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [currency, setCurrency] = useState(""); // Added state for currency
  const deviceId = localStorage.getItem("device_id");

  useEffect(() => {
    fetchCartProducts();
    // Fetch currency when the component mounts
    const fetchCurrency = async () => {
      try {
        const response = await axios.get(`${BASE_URL}/user/store-setting`, {
          headers: {
            Authorization: `Bearer ${API_TOKEN}`,
            "Content-Type": "application/json",
          },
        });

        const currencysymbol =
          response.data.user_meta_data.settings.currency_symbol;
        setCurrency(currencysymbol);
      } catch (error) {
        console.error("Error fetching currency:", error);
      }
    };

    fetchCurrency();
  }, []);

  const fetchCartProducts = async () => {
    setIsLoading(true);
    try {
      const response = await axios.get(
        `${BASE_URL}/customer/show-cart-products?device_id=${deviceId}&slink=${SLINK}`
      );

      if (
        response.data.productDetails &&
        Array.isArray(response.data.productDetails)
      ) {
        setCartProducts(response.data.productDetails);
      } else {
        console.error("Unexpected response format");
      }
    } catch (error) {
      console.error("Error fetching cart products:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const addToCart = async (productId) => {
    try {
      const response = await axios.post(
        `${BASE_URL}/customer/store-products-to-cart`,
        {
          catalog_id: "507", // Adjust as needed
          product_id: productId,
          notes: "order",
          slink: SLINK,
          device_id: deviceId,
        }
      );
      console.log("Response:", response.data);

      if (response.data && response.data.message === "success") {
        fetchCartProducts(); // Refresh cart products
      } else {
        console.error("Failed to add item to cart:", response.data);
      }
    } catch (error) {
      console.error("Error adding to cart:", error);
    }
  };

  const removeFromCart = async (cartProductId) => {
    try {
      console.log("delete", cartProductId);
      const response = await axios.delete(
        `${BASE_URL}/customer/delete-cart-products`,
        {
          params: {
            cart_product_id: cartProductId,
            device_id: deviceId,
            slink: SLINK,
          },
        }
      );

      if (response.data && response.data.message === "success") {
        fetchCartProducts(); // Refresh cart products
      } else {
        console.error("Failed to remove item from cart:", response.data);
      }
    } catch (error) {
      console.error("Error removing from cart:", error);
    }
  };

  const updateCartItemQuantity = async (cartProductId, newQuantity) => {
    console.log(
      "Updating cart product with ID:",
      cartProductId,
      "to quantity:",
      newQuantity
    );
    try {
      const response = await axios.put(
        `${BASE_URL}/customer/update-cart-products`,
        null,
        {
          params: {
            cart_product_id: cartProductId,
            device_id: deviceId,
            slink: SLINK,
            qty: newQuantity,
          },
        }
      );

      if (response.data && response.data.message === "success") {
        console.log("Cart item quantity updated successfully.");
        fetchCartProducts(); // Refresh cart products after update
      } else {
        console.error("Failed to update cart item quantity:", response.data);
      }
    } catch (error) {
      console.error("Error updating cart:", error);
      console.error("Request parameters:", {
        cart_product_id: cartProductId,
        device_id: deviceId,
        slink: SLINK,
        qty: newQuantity,
      });
    }
  };

  return (
    <CartContext.Provider
      value={{
        cartProducts,
        setCartProducts,
        isLoading,
        setIsLoading,
        removeFromCart,
        addToCart,
        updateCartItemQuantity,
        currency, // Added currency to context
      }}
    >
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => React.useContext(CartContext);
